import React, { useEffect, useState } from 'react';
import { Play, Pause, RotateCcw, Clock } from 'lucide-react';
import { Button } from './Button';
import { motion } from 'framer-motion';
interface FocusTimerProps {
  initialDuration?: number; // in minutes
  onComplete?: () => void;
  onDurationChange?: (duration: number) => void;
}
export function FocusTimer({
  initialDuration = 25,
  onComplete,
  onDurationChange
}: FocusTimerProps) {
  const [duration, setDuration] = useState(initialDuration);
  const [timeLeft, setTimeLeft] = useState(initialDuration * 60);
  const [isActive, setIsActive] = useState(false);
  const [showDurationPicker, setShowDurationPicker] = useState(false);
  const presetDurations = [15, 25, 45, 60, 90, 120];
  useEffect(() => {
    let interval: any = null;
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft => timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsActive(false);
      onComplete?.();
    }
    return () => clearInterval(interval);
  }, [isActive, timeLeft, onComplete]);
  const toggleTimer = () => setIsActive(!isActive);
  const resetTimer = () => {
    setIsActive(false);
    setTimeLeft(duration * 60);
  };
  const changeDuration = (newDuration: number) => {
    setDuration(newDuration);
    setTimeLeft(newDuration * 60);
    setIsActive(false);
    setShowDurationPicker(false);
    onDurationChange?.(newDuration);
  };
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  const progress = (duration * 60 - timeLeft) / (duration * 60) * 100;
  return <div className="flex flex-col items-center space-y-8" data-id="element-238">
      <div className="relative w-64 h-64 flex items-center justify-center" data-id="element-239">
        {/* Outer Ring */}
        <div className="absolute inset-0 rounded-full border-8 border-gray-100 dark:border-gray-800" data-id="element-240"></div>

        {/* Animated Progress Ring */}
        <svg className="absolute inset-0 w-full h-full -rotate-90" data-id="element-241">
          <circle cx="128" cy="128" r="120" stroke="currentColor" strokeWidth="8" fill="none" className="text-game-teal" strokeDasharray={`${2 * Math.PI * 120}`} strokeDashoffset={`${2 * Math.PI * 120 * (1 - progress / 100)}`} strokeLinecap="round" style={{
          transition: 'stroke-dashoffset 1s linear'
        }} data-id="element-242" />
        </svg>

        <div className="text-center z-10" data-id="element-243">
          <motion.div key={timeLeft} initial={{
          scale: 1
        }} animate={{
          scale: timeLeft % 60 === 0 && isActive ? [1, 1.05, 1] : 1
        }} transition={{
          duration: 0.3
        }} className="text-6xl font-fredoka font-bold text-game-dark dark:text-white tabular-nums" data-id="element-244">
            {formatTime(timeLeft)}
          </motion.div>
          <p className="text-gray-500 dark:text-gray-400 font-quicksand mt-2" data-id="element-245">
            {isActive ? 'Stay Focused!' : 'Focus Time'}
          </p>
        </div>
      </div>

      <div className="flex gap-4" data-id="element-246">
        <Button variant={isActive ? 'secondary' : 'primary'} onClick={toggleTimer} size="lg" className="w-32" icon={isActive ? <Pause className="w-5 h-5" data-id="element-248" /> : <Play className="w-5 h-5" data-id="element-249" />} data-id="element-247">
          {isActive ? 'Pause' : 'Start'}
        </Button>
        <Button variant="ghost" onClick={resetTimer} className="!p-4 rounded-full bg-gray-100 dark:bg-gray-800" disabled={isActive} data-id="element-250">
          <RotateCcw className="w-5 h-5" data-id="element-251" />
        </Button>
        <Button variant="ghost" onClick={() => setShowDurationPicker(!showDurationPicker)} className="!p-4 rounded-full bg-gray-100 dark:bg-gray-800" disabled={isActive} data-id="element-252">
          <Clock className="w-5 h-5" data-id="element-253" />
        </Button>
      </div>

      {showDurationPicker && !isActive && <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="w-full max-w-md" data-id="element-254">
          <div className="bg-white dark:bg-[#252936] rounded-2xl p-4 shadow-lg border border-gray-100 dark:border-gray-800" data-id="element-255">
            <h3 className="font-fredoka font-bold text-game-dark dark:text-white mb-3 text-center" data-id="element-256">
              Choose Duration
            </h3>
            <div className="grid grid-cols-3 gap-2" data-id="element-257">
              {presetDurations.map(mins => <button key={mins} onClick={() => changeDuration(mins)} className={`
                    p-3 rounded-xl font-fredoka font-bold transition-all
                    ${duration === mins ? 'bg-game-purple text-white shadow-md' : 'bg-gray-100 dark:bg-gray-800 text-game-dark dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700'}
                  `} data-id="element-258">
                  {mins} min
                </button>)}
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700" data-id="element-259">
              <label className="block text-sm font-quicksand font-bold text-gray-600 dark:text-gray-400 mb-2" data-id="element-260">
                Custom Duration (minutes)
              </label>
              <div className="flex gap-2" data-id="element-261">
                <input type="number" min="1" max="240" placeholder="e.g., 30" className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-game-dark dark:text-white font-quicksand" onKeyDown={e => {
              if (e.key === 'Enter') {
                const value = parseInt((e.target as HTMLInputElement).value);
                if (value >= 1 && value <= 240) {
                  changeDuration(value);
                }
              }
            }} data-id="element-262" />
                <Button size="sm" onClick={e => {
              const input = e.currentTarget.previousElementSibling as HTMLInputElement;
              const value = parseInt(input.value);
              if (value >= 1 && value <= 240) {
                changeDuration(value);
              }
            }} data-id="element-263">
                  Set
                </Button>
              </div>
            </div>
          </div>
        </motion.div>}
    </div>;
}